import { useState, useEffect, useMemo, useCallback, useRef, use } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import config from "../config/config";
import { TransactionFactory } from "../factory/transactionFactory";
import { requestService } from "../requests/requestService";
import { sharedData } from "../sharedData/sharedData";
import { userSession } from "../observers/userObserver";

import { fetchTransactions, fetchTransactionDetails, updateTransaction , fetchQuickfillTransactionDetails } from "../components/transactionapi";
import { TransactionApiFactory } from "../apiFactory/transactionApiFactory";
import { useValidateTransaction } from "../validations/validationHooks/useValidateTransaction";
import { createPrivateEvent } from "../utils/utils";
import { AlreadyValidated, ValidatedSuccessfully, SavedSuccessfully, UpdatedSuccessfully, Saving, SkipSaving } from "../components/popups/popupMessages";
import { reloadAfterValidate } from "../utils/reloadAfterValidate";

export const useTransactionData = () => {
  const fetchedRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if in view/readonly mode from URL
  const isViewMode = useMemo(() => searchParams.get("view") === "true", [searchParams]);
  const isReadOnlyMode = useMemo(() => searchParams.get("readonly") === "true", [searchParams]);

  const [isReadOnly, setIsReadOnly] = useState(isViewMode || isReadOnlyMode);
  const [isSelectorReadOnly, setIsSelectorReadOnly] = useState(searchParams.get("readonly") === "true");

  const isView = useMemo(() => searchParams.get("view") === "true", [searchParams]);
  const isEdit = useMemo(() => searchParams.get("edit") === "true", [searchParams]);

  const [requestId, setRequestId] = useState(useMemo(()=>{return sharedData.get("requestId")}, []) || '');
  //const transactionId = useMemo(()=>{return sharedData.get("transactionId")}, []);
  const entityId = useMemo(()=>{return sharedData.get("entityId")},[]);
  // const { requestId, transactionId: initialTransactionId, status, entityId, propertyId } = location.state || {};

  const [formData, setFormData] = useState(()=>{
    const savedFormData = localStorage.getItem("transaction");
    return savedFormData ? JSON.parse(savedFormData) : TransactionFactory({});
  });

  // Subscribe to requestId changes to detect new requests
  useEffect(() => {
    const unsubscribe = sharedData.subscribe("requestId", (newRequestId) => {
      console.log("📢 RequestId changed to:", newRequestId);
      setRequestId(newRequestId);

      // If requestId changed but no transactionId, it's a new blank request
      const sharedTransactionId = sharedData.get("transactionId");
      if (newRequestId && !sharedTransactionId) {
        console.log("🧹 New request detected, clearing form data");
        setFormData(TransactionFactory({}));
        localStorage.removeItem("transaction");
        const isReadOnlyFromURL = searchParams.get("readonly") === "true" || searchParams.get("view") === "true";
        setIsReadOnly(isReadOnlyFromURL);
        setIsDisabled(false);
        setIsTransactionCompleted(false);
        fetchedRef.current = false;
      }
    });

    return () => unsubscribe();
  }, []);

 // const [transactionId, setTransactionId] = useState('');
  const [existingTransactions, setExistingTransactions] = useState([]);
  const [isTransactionCompleted, setIsTransactionCompleted] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalHeading, setModalHeading] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTransactionConfirmModal, setShowTransactionConfirmModal] = useState(false);
  const [hasShownTransactionModal, setHasShownTransactionModal] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { validateTransaction } = useValidateTransaction();
  
 

  

  // ✅ Clean checkIsReadOnly (deduplicated)
  const checkIsReadOnly = useCallback((details) => {
    const isEditMode = searchParams.get("edit") === "true";
    const completed = details?.status === "Validated";
    const readonlyFromUrl = searchParams.get("readonly") === "true";
    const viewFromUrl = searchParams.get("view") === "true";
    setIsTransactionCompleted(completed);
    // If in view mode or readonly mode, always return true
    return viewFromUrl || readonlyFromUrl || completed;
  }, [searchParams]);

  const getTransactionDetails = useCallback(async (transactionId) => {
    console.log("🔄 Fetching transaction details for ID:", transactionId);
    if (!transactionId) {
      setFormData(TransactionFactory({}));
      localStorage.removeItem("transaction");
      const isReadOnlyFromURL = searchParams.get("readonly") === "true" || searchParams.get("view") === "true";
      setIsReadOnly(isReadOnlyFromURL);
      setIsDisabled(false);
      setIsTransactionCompleted(false);
      return;
    }
    try {
      const details = await fetchTransactionDetails(transactionId);
      if (details) {
        const updatedFormData = TransactionFactory(details);
        setFormData(updatedFormData);
        localStorage.setItem("transaction", JSON.stringify(updatedFormData));
        sharedData.set("transactionType", updatedFormData.transactionType);
        const isReadOnly = checkIsReadOnly(updatedFormData);
        setIsReadOnly(isReadOnly);
        setIsDisabled(isReadOnly)
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
  }, []);





  const getQuickTransactionDetails = useCallback(async (transactionId) => {
    console.log("🔄 Fetching transaction details for ID:", transactionId);
    if (!transactionId) {
      setFormData(TransactionFactory({}));
      localStorage.removeItem("transaction");
      const isReadOnlyFromURL = searchParams.get("readonly") === "true" || searchParams.get("view") === "true";
      setIsReadOnly(isReadOnlyFromURL);
      setIsDisabled(false);
      setIsTransactionCompleted(false);
      return;
    }
    try {
      const details = await fetchQuickfillTransactionDetails(transactionId);
      if (details) {
        const updatedFormData = TransactionFactory(details);
        setFormData(updatedFormData);
        localStorage.setItem("transaction", JSON.stringify(updatedFormData));
        sharedData.set("transactionType", updatedFormData.transactionType);
        const isReadOnly = checkIsReadOnly(updatedFormData);
        setIsReadOnly(isReadOnly);
        setIsDisabled(isReadOnly)
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
  }, []);


  useEffect(() => {
    const sharedTransactionId = sharedData.get("transactionId");
    const localStorageData = localStorage.getItem("transaction");
    const storedTransaction = localStorageData ? JSON.parse(localStorageData) : null;

    // If no transactionId in sharedData but there's old data in localStorage, clear it
    if (!sharedTransactionId && storedTransaction?.transactionId) {
      console.log("🧹 Clearing old transaction data for new request");
      setFormData(TransactionFactory({}));
      localStorage.removeItem("transaction");
      fetchedRef.current = false;
      setIsDataLoaded(true);
      return;
    }
    // Only fetch if:
    // 1. We have a shared transaction ID
    // 2. It's different from current form data
    // 3. We haven't fetched it before
    else if (sharedTransactionId &&
        sharedTransactionId !== storedTransaction?.transactionId &&
        !fetchedRef.current
    ) {
      fetchedRef.current = true;
      getTransactionDetails(sharedTransactionId);
    }

    // Mark data as loaded after initial check
    setIsDataLoaded(true);
  }, [getTransactionDetails, requestId]);

  // Check if we should show the transaction confirmation modal
  useEffect(() => {
    // Wait until data is loaded before checking
    if (!isDataLoaded) return;

    const isViewMode = searchParams.get("view") === "true";
    const isReadOnlyMode = searchParams.get("readonly") === "true";

    // Check if transaction ID exists in formData or sharedData
    const sharedTransactionId = sharedData.get("transactionId");
    const hasTransactionId = (formData.transactionId !== undefined &&
                              formData.transactionId !== null &&
                              formData.transactionId !== "" &&
                              formData.transactionId !== "None" &&
                              formData.transactionId !== 0) ||
                             (sharedTransactionId !== undefined &&
                              sharedTransactionId !== null &&
                              sharedTransactionId !== "" &&
                              sharedTransactionId !== "None" &&
                              sharedTransactionId !== 0 &&
                              sharedTransactionId !== "N/A");

    console.log("🔍 Transaction Modal Check:", {
      isDataLoaded,
      isViewMode,
      isReadOnlyMode,
      hasTransactionId,
      transactionId: formData.transactionId,
      sharedTransactionId,
      transactionIdType: typeof formData.transactionId,
      hasShownTransactionModal,
      formDataKeys: Object.keys(formData)
    });

    // Only show modal if:
    // 1. Not in view mode
    // 2. Not in readonly mode
    // 3. No transaction ID exists (in formData or sharedData)
    // 4. Haven't shown the modal yet in this session
    if (!isViewMode && !isReadOnlyMode && !hasTransactionId && !hasShownTransactionModal) {
      console.log("✅ Showing transaction confirmation modal");
      setShowTransactionConfirmModal(true);
      setHasShownTransactionModal(true);
    }
  }, [formData.transactionId, searchParams, hasShownTransactionModal, isDataLoaded]);

  // useEffect(() => {
  //   const sharedRequestId = sharedData.get("requestId");
  
  //   if (sharedRequestId) {
  //     const checkRiskContinued = async () => {
  //       try {
        
  //         const response = await fetch(`${config["backend-url"]}/check_risk_Continued_status`, {
  //           method: "POST",
  //           headers: {
  //             "Content-Type": "application/json",
  //             Authorization: `Bearer ${userSession.getToken()}`,
  //           },
  //           body: JSON.stringify({ request_id: sharedRequestId }),
  //         });
  
  //         if (!response.ok) throw new Error("Failed to check risk continued status");
  
  //         const result = await response.json();
  //         console.log("✅ Risk Continued Status:", result.exists); // handle result as needed
  //       } catch (error) {
  //         console.error("❌ Error checking risk continued status:", error);
  //       }
  //     };
  
  //     checkRiskContinued();
  //   }
  // }, []);
  

  // const mountedRef = useRef(false);

  // useEffect(() => {
  //   const sharedTransactionId = sharedData.get("transactionId");
  //   const localStorageData = localStorage.getItem("transaction");
  //   const storedTransaction = localStorageData ? JSON.parse(localStorageData) : null;
  
  //   if (!mountedRef.current) {
  //     // On first mount only
  //     mountedRef.current = true;
  
  //     if (sharedTransactionId) {
  //       // Fetch full transaction details on first mount
  //       getTransactionDetails(sharedTransactionId);
  //     }
  //   } else if (
  //     sharedTransactionId &&
  //     sharedTransactionId !== storedTransaction?.transactionId
  //   ) {
  //     // On subsequent updates
  //     getQuickTransactionDetails(sharedTransactionId);
  //   }
  // }, [getTransactionDetails, getQuickTransactionDetails]);
  
  
  const validatePage = async () => {
    // Block validate when Term contains a decimal. Allowed to type freely,
    // but can't validate / lock with a fractional Term value.
    if (typeof formData.term === "string" && /[.,]/.test(formData.term)) {
      setModalHeading("Validation Result");
      setModalMessage(
        <strong>Term must be a whole number. Please remove the decimal value before validating.</strong>
      );
      setModalOpen(true);
      return;
    }

    setModalHeading("Validation Result");
    try {
      const localStorageData = localStorage.getItem("transaction");
      const storedTransaction = localStorageData ? JSON.parse(localStorageData) : null;
      // const storedTransaction = localStorageData ? JSON.parse(localStorageData) : null;

  if (storedTransaction && storedTransaction.status === "Validated") {
    setModalMessage(<AlreadyValidated page={"Transaction Details"} />);
    setModalOpen(true);
    return;
  }
      // if (storedTransaction && storedTransaction.status === "Validated") {
      //   setModalMessage( <AlreadyValidated page={"Transaction Details"}/> )
      //   setModalOpen(true);
      //   return;
      // }
      setModalMessage( <strong>Validating...</strong>);
      setModalOpen(true);

      if (storedTransaction?.status === "Validated") {
        setModalMessage(<strong>Already Validated, Move to next page.</strong>);
        return;
      }
      const errors = validateTransaction(formData);

      if (errors) {
        setModalMessage(
          <>
          <strong>Please fill in the following required fields:</strong>
          <br />
          <div style={{ "maxHeight": "200px", "overflow": "auto" }}>
          {Object.keys(errors).map((field) => (
            <div key={field}>
              <ul>
                {errors[field].map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          ))}
          </div>
        </>
        );
        setModalOpen(true);
        setLoading(false);
        return;
      }
    const payload = TransactionApiFactory(requestId, storedTransaction);  
      // ✅ Always update the transaction
      const updatedTransaction = await updateTransaction(payload);
  
      let transactionId = updatedTransaction?.transaction_id;
  
      if (transactionId) {
        setFormData((prevData) => ({
          ...prevData,
          transactionID: transactionId,
        }));
  
        const details = await fetchTransactionDetails(transactionId);

        if (details) {
          const currentRequestId = requestId || sharedData.get("requestId");
          const allTransactions = await fetchTransactions(currentRequestId);
          setExistingTransactions(allTransactions);
          const mergedFormData = TransactionFactory({...details,transactionId})
          sharedData.set("transactionId", transactionId);
          sharedData.set("transactionType", mergedFormData.transactionType);
  
          setFormData(mergedFormData);
          localStorage.setItem("transaction", JSON.stringify(mergedFormData));
  
          const isReadOnly = checkIsReadOnly(details);
          setIsReadOnly(isReadOnly);
          setIsDisabled(isReadOnly);
          setHasUnsavedChanges(false);
        }
      } else {
        setLoading(false);
        return;
      }
  
      // ✅ Validate transaction
      const validatePayload = {
        ...storedTransaction,
        transactionId,
      };
  
      const token = userSession.getToken();
  
      const response = await fetch(`${config["backend-url"]}/validate-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(validatePayload),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setModalMessage( <ValidatedSuccessfully page={"Transaction Details"}/>);
        setModalOpen(true);

        const details = await fetchTransactionDetails(transactionId);
        if (details) {
          const currentRequestId = requestId || sharedData.get("requestId");
          const allTransactions = await fetchTransactions(currentRequestId);
          setExistingTransactions(allTransactions);
          const validatedFormData = TransactionFactory({...details,transactionId});

          setFormData(validatedFormData);
          sharedData.set("transactionId", transactionId);
          sharedData.set("transactionType", validatedFormData.transactionType);
          localStorage.setItem("transaction", JSON.stringify(validatedFormData));
          await updateRequestTable(); // ✅ Call update request table API

          const isReadOnly = checkIsReadOnly(details);
          setIsReadOnly(isReadOnly);
          setIsDisabled(isReadOnly);
        }
        reloadAfterValidate();
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  
  const handleTransactionChange = useCallback(async (e) => {
    const transactionId = e.target.value;
    setHasUnsavedChanges(true);
    sharedData.set("transactionId", transactionId);
  
    if (transactionId !== "None" && transactionId !== "") {
      // ✅ Fetch details for selected transaction
      console.log("🔄 Fetching transaction details for ID: newapi", );
      await getQuickTransactionDetails(transactionId);
    } else {
      // ✅ Clear current transaction data
      const resetFormData = TransactionFactory({});
      localStorage.removeItem("transaction");
      sharedData.delete("transactionId");
  
      setFormData(resetFormData);
      setIsDisabled(false);
      const isReadOnlyFromURL = searchParams.get("readonly") === "true" || searchParams.get("view") === "true";
      setIsReadOnly(isReadOnlyFromURL);
      setIsTransactionCompleted(false);
    }
  }, [getQuickTransactionDetails, searchParams]);
 
  const updateRequestTable = useCallback(async () => {
     try {
       // Get requestId from sharedData as fallback to state
       const currentRequestId = requestId || sharedData.get("requestId");

       if (!currentRequestId) {
         console.error("❌ Request ID is missing in both state and sharedData");
         setModalMessage("Request ID is missing. Cannot update request table.");
         return;
       }

       console.log("✅ Updating request table with requestId:", currentRequestId);
       // ✅ Get transactionId from sharedData
       const transactionId = sharedData.get("transactionId");
       const data = { requestId: currentRequestId, transactionId };
       await requestService.updateRequest(data);
     } catch (error) {
      setModalMessage(` Failed to update request table: ${error.message}`);
     }
   }, [requestId]);
  const handleSubmit = useCallback(async (handleFor = "save") => {

    // Block save when Term contains a decimal. Allowed to type freely, but
    // can't persist a fractional Term value.
    if (typeof formData.term === "string" && /[.,]/.test(formData.term)) {
      setModalHeading("");
      setModalMessage(
        <strong>Term must be a whole number. Please remove the decimal value before saving.</strong>
      );
      setModalOpen(true);
      return;
    }

    setModalHeading("");
    setModalMessage(<Saving page={"Transaction Details"}/>);
    setModalOpen(true);

    setHasUnsavedChanges(false);
    sharedData.set("transactionId", formData.transactionId);
    // ✅ Step 0: If transaction is already completed, update request table directly
    if (isTransactionCompleted) {
      console.log("⚠️ Transaction already completed. Skipping submit...");
      setModalHeading("")
      setModalMessage( <SkipSaving page={"Transaction Details"} /> )
      await updateRequestTable(); // ✅ Call update request table API
      return;
    }

    setLoading(true); // ✅ Start loading

    try {

      const payload = TransactionApiFactory(requestId, formData);

      // ✅ Step 1: Update the transaction
      const updatedTransaction = await updateTransaction(payload);

      if (updatedTransaction?.transaction_id) {
        // ✅ Step 2: Fetch the updated transaction using transaction ID
        const details = await fetchTransactionDetails(updatedTransaction.transaction_id);
        sharedData.set("transactionId", updatedTransaction.transaction_id);
        console.log("✅ Fetched updated transaction details:", details);

        if (details) {

          
          // ✅ Step 3: Update state with fetched data
          const updatedFormData = TransactionFactory(details);
          sharedData.set("transactionId", updatedFormData.transactionId);
          setFormData(updatedFormData); // ✅ Update state
          localStorage.setItem("transaction", JSON.stringify(updatedFormData)); // ✅ Save to localStorage

          // ✅ Update state for read-only based on status
          const isReadOnly = checkIsReadOnly(details);
          setIsReadOnly(isReadOnly);
          setIsDisabled(isReadOnly);


          // ✅ Update request table with transaction ID after update
          await updateRequestTable();
          setModalMessage( <SavedSuccessfully page={"Transaction Details"}/>);
        }

        // ✅ Step 4: Call fetchTransactions after updating transaction
        console.log("🔄 Fetching updated transaction list...");
        const allTransactions = await fetchTransactions(); // 🔥 Additional call to fetch all transactions
        console.log("✅ Fetched updated transaction list:", allTransactions);

        // ✅ Optionally update state with fetched transactions
        setExistingTransactions(allTransactions);
      }
    } catch (error) {
      console.error("❌ Error updating transaction:", error);

      if (handleFor === "save") {
          // ✅ Better error message handling
          setModalHeading("");
          setModalMessage( <strong>❌ Error updating transaction <br/> ${error.response?.data?.message || error.message || "Unknown error"}</strong>);
        }
    } finally {
      setLoading(false); // ✅ Stop loading regardless of success or failure
    }
  }, [checkIsReadOnly, formData, isTransactionCompleted, requestId, updateRequestTable]);

  const handleNext = useCallback(async () => {

    // if (!hasUnsavedChanges) {
    //   const newUrl = `/property-info?${searchParams.toString()}`;
    //   navigate(newUrl);
     
    //   return;
    // }
  try {
    sharedData.set("transactionId", formData.transactionId);
    await updateRequestTable(); // ✅ Call update request table API
    if (!isTransactionCompleted) {
      console.log("🔄 Saving form data...");
      await handleSubmit("next");
      console.log("✅ Data saved successfully.");
    } else {
      console.log("⚠️ Transaction already completed. Skipping save...");
    }

    // add searchParams to the new URL, add requestId and transactionId
    // const requestId = searchParams.get("requestId");
    const newUrl = `/property-info?${searchParams.toString()}`;
    navigate(newUrl);
  } catch (error) {
    console.error("❌ Error:", error);
    setModalMessage(`Error: ${error.response?.data?.message || error.message || "Unknown error"}`);
    setModalOpen(true);
  }
  }, [formData.transactionId, handleSubmit, isTransactionCompleted, navigate, searchParams, updateRequestTable]);

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        if (requestId) {
          const data = await fetchTransactions(requestId);
          setExistingTransactions(data);
          console.log("Transactions fetched:", data);
        }
      } catch (error) {
        console.log(error);
        console.error("Failed to load transactions:", error);
      }
    };

    loadTransactions();
  }, [requestId]);


  const updateFormData = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value };
      localStorage.setItem("transaction", JSON.stringify(newFormData));
      return newFormData;
    });
  };

  const handleSpecialCases = useCallback((event) => {
    const { name, value } = event.target;
    if (name === "transactionType") {

      if (value === "Sale") {
        updateFormData(createPrivateEvent("purchasePrice",""));
        updateFormData(createPrivateEvent("ratePerAnnum",""));
        updateFormData(createPrivateEvent("term",""));
      } else if (value === "Purchase") {
        updateFormData(createPrivateEvent("salePrice",""));
        updateFormData(createPrivateEvent("ratePerAnnum",""));
        updateFormData(createPrivateEvent("term",""));
      } else if (value === "Letting") {
        updateFormData(createPrivateEvent("salePrice",""));
        updateFormData(createPrivateEvent("purchasePrice",""));
      }
    } else if (name === "transactionId") {
      sharedData.set("transactionId", value);
    }
  },[]);

  const handleChange = (e) => {
    setHasUnsavedChanges(true);
    updateFormData(e);
    handleSpecialCases(e);
    if (e.target.name === "transactionType") {
      sharedData.set("transactionType", e.target.value);
    }
  };

  // Handle "Yes" - user has a transaction, stay on the page
  const handleTransactionConfirmYes = useCallback(() => {
    setShowTransactionConfirmModal(false);
    // User stays on the page to fill the form
  }, []);

  // Handle "No" - user doesn't have a transaction, redirect to next page
  const handleTransactionConfirmNo = useCallback(() => {
    setShowTransactionConfirmModal(false);
    // Navigate to the next page (property-info)
    const newUrl = `/property-info?${searchParams.toString()}`;
    navigate(newUrl);
  }, [navigate, searchParams]);


    return {
    isSelectorReadOnly,
    searchParams,
    requestId,
    formData,
    existingTransactions,
    isTransactionCompleted,
    isReadOnly,
    isDisabled,
    modalOpen,
    modalMessage,
    modalHeading,
    hasUnsavedChanges,
    loading,
    checkIsReadOnly,
    handleChange,
    setModalOpen,
    setFormData,
    handleSubmit,
    setHasUnsavedChanges,
    setModalMessage,
    setIsDisabled,
    handleTransactionChange,
    setIsReadOnly,
    validatePage,
    navigate,
    handleNext,
    entityId,
    showTransactionConfirmModal,
    handleTransactionConfirmYes,
    handleTransactionConfirmNo
  };
};
